/* eslint-disable @typescript-eslint/no-explicit-any */

// Imports for external libraries go here.
import React, { FC, useEffect, useRef, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { SearchResultsFilterPanelProps } from './SearchResultsFilterPanel.types';
import { StyledSearchResultsFilterPanel } from './SearchResultsFilterPanel.styles';
import { CheckboxControlsList, DropDownModal, Modal } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { BrandFilter } from '../BrandFilter/BrandFilter';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import {
  fetchHDData,
  filterAvailableFilters,
  getFilteredOrder,
  mapActivitiesToBrandsStructure,
  totalFilterCount,
  updateFiltersPayload,
} from '../../modules/helpers';
import { useApolloClient } from '@apollo/client';
import {
  phoenixLuxuryDatedSearchByLocationMap,
  phoenixLuxuryDatedSearchByLocationMapFilters,
} from '../MapView/queries';
import {
  DEFAULT_FILTERS_ORDER,
  FILTERS_MAPPING,
  LOCATION,
  LOCATIONS,
  MOBILE_COL_LENGTH,
  COL_LENGTH,
} from '../../modules/constants/constant';
import { DropDownComponent } from '../DropDownComponent';

// Use named rather than default exports.
export const SearchResultsFilterPanel: FC<SearchResultsFilterPanelProps> = ({
  filterProps,
  facets,
  filtersPayload,
  setFiltersPayload,
  popupOpenState,
  setPopupOpenState,
  setFacets,
  setIsLoading,
  setMarkers,
  setFiltersCount,
  brandsClicked,
  setBrandsClicked,
  setSelectedRegion,
  filterButtonClicked,
  setFilterButtonClicked,
  viewPort,
  resetLabel,
  doneLabel,
}) => {
  const client = useApolloClient();

  const clientEnvVars = useClientEnvVarsStore.getState().envVarsObject;
  const { ACCEPT_LANGUAGE: currentLocale, REQUEST_ID: requestID } = clientEnvVars;

  //REF's here ....
  const prevfiltersRef = useRef<any>([]);
  const filtersRef = useRef<any>();
  const filterPanelRef = useRef<any>(null);
  const dropdownRef = useRef<any>(null);

  filtersRef.current = filtersPayload;
  const prevfiltersRefVal = filtersRef.current;

  //State's Here....
  const [defaultSelectedFilters, setDefaultSelectedFilters] = useState<any>(filtersPayload);

  useEffect(() => {
    prevfiltersRef.current = filtersPayload;
  }, [popupOpenState]);

  const getData = async (query: any, variables: any, applyBtnClicked = false) => {
    applyBtnClicked && setIsLoading(true);

    const data = await fetchHDData(client, query, {
      variables: variables,
      context: {
        headers: {
          'x-request-id': requestID,
          'accept-language': currentLocale?.replace('_', '-'),
        },
      },
    });

    const searchResultsFacets =
      data?.search?.properties?.searchByLocation?.facets ?? data?.search?.properties?.searchByGeolocation?.facets ?? [];

    const searchResults =
      data?.search?.properties?.searchByLocation?.edges ?? data?.search?.properties?.searchByGeolocation?.edges ?? [];

    setFacets(searchResultsFacets);

    applyBtnClicked && setMarkers(searchResults);
    applyBtnClicked && setIsLoading(false);
  };

  // update payload on selection of any filters
  const handleFiltersSelectionChange = (code: string, filterObjList: any) => {
    let temp: any = [];
    if (code === FILTERS_MAPPING?.Regions) {
      temp?.push(filterObjList?.code);
    } else if (code === FILTERS_MAPPING?.City || code === FILTERS_MAPPING?.State || code === FILTERS_MAPPING?.Country) {
      temp?.push(filterObjList?.code);
    } else if (code === FILTERS_MAPPING?.Brands) {
      filterObjList?.length > 0
        ? filterObjList?.forEach((obj: any) => {
            temp?.push(obj?.brandTagId);
          })
        : temp.push(...(filterProps?.brandTags || []));

      temp = [...new Set(temp)];

      filterObjList?.length > 0 && filterProps?.brandTags && filterProps?.brandTags?.length > 1
        ? setBrandsClicked(true)
        : setBrandsClicked(false);
    } else if (code === FILTERS_MAPPING?.Activities) {
      filterObjList?.forEach((obj: any) => {
        temp?.push(obj?.brandTagId);
      });
      temp = [...new Set(temp)];
    } else {
      filterObjList?.forEach((obj: any) => temp?.push(obj?.code));
    }

    const payload = defaultSelectedFilters.map((obj: any) => {
      if (obj?.type === code) {
        return {
          ...obj,
          dimensions: temp,
        };
      } else {
        return obj;
      }
    });

    const variables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...payload],
        },
      },
      limit: 20,
      offset: 0,
      sort: null,
    };

    if (
      (code === FILTERS_MAPPING?.Regions ||
        code === FILTERS_MAPPING.Country ||
        code === FILTERS_MAPPING.City ||
        code === FILTERS_MAPPING.State) &&
      viewPort === 'viewPortL'
    ) {
      getData(phoenixLuxuryDatedSearchByLocationMap, variables, true);

      // Calculate total filter count
      const filterCount = totalFilterCount(payload, brandsClicked);
      setFiltersCount(filterCount);
      setDefaultSelectedFilters(payload);
      setFiltersPayload(payload);
      setFilterButtonClicked('');
      setSelectedRegion(filterObjList?.code);
    } else {
      getData(phoenixLuxuryDatedSearchByLocationMapFilters, variables);
      setDefaultSelectedFilters(payload);
    }
  };

  // show default selected filters on modal
  const showDefaultSelectedFilters = (filterData: any): any => {
    const result: any[] = [];

    if (!filterData?.buckets || !defaultSelectedFilters) {
      return result;
    }

    // Create a map for faster lookup of default filters by type
    const filterMap = new Map<string, string[]>();
    defaultSelectedFilters?.forEach((filter: { dimensions: string[]; type: string }) => {
      if (filter?.type === FILTERS_MAPPING?.Brands) {
        const hasBrandTags = !filterProps?.brandTags ? true : filterProps?.brandTags?.length > 0;
        if ((brandsClicked && hasBrandTags) || !hasBrandTags) {
          filterMap?.set(filter.type, filter.dimensions);
        }
      } else if (filter?.type !== FILTERS_MAPPING?.Brands && filter?.dimensions?.length) {
        filterMap?.set(filter?.type, filter?.dimensions);
      } else {
        filterMap?.set(filter?.type, []);
      }
    });

    filterData?.buckets?.forEach((data: any) => {
      const brandDimensions = filterMap?.get(FILTERS_MAPPING?.Brands);
      const activitiesDimensions = filterMap?.get(FILTERS_MAPPING?.Activities);

      // Handle brand filters & Activities Filter's
      if (brandDimensions && brandDimensions?.includes(data?.code)) {
        result?.push({
          brandTagId: data?.code,
          brandTagTitle: data?.label,
        });
      } else if (activitiesDimensions && activitiesDimensions?.includes(data?.code)) {
        result?.push({
          brandTagId: data?.code,
          brandTagTitle: data?.label,
        });
      } else {
        // Handle other filter types
        filterMap?.forEach(dimensions => {
          if (dimensions?.includes(data?.code)) {
            result?.push({
              code: data?.code,
              label: data?.label,
            });
          }
        });
      }
    });
    return result;
  };

  // to get all the brands in enabled and disabled state based on the UXL response
  const getCategorizedBrands = (currentFilterData: any, categorizedBrandsArray: any) => {
    const updatedCategorizedArr = categorizedBrandsArray?.map((item: any) => {
      const updatedBrandListItem = item?.brandsLabelListItems?.map((brand: any) => {
        const matchingFacet = currentFilterData?.find((facet: any) => facet?.code === brand?.brandTagId);
        if (matchingFacet) {
          return { ...brand, ...matchingFacet, isDisabled: false };
        } else {
          return { ...brand, ...matchingFacet, isDisabled: true };
        }
      });
      return { ...item, brands: updatedBrandListItem };
    });

    return updatedCategorizedArr;
  };

  // apply filters on click of apply button on modal
  const applyBtnHandler = () => {
    const variables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...defaultSelectedFilters],
        },
      },
      limit: 20,
      offset: 0,
      sort: null,
    };

    getData(phoenixLuxuryDatedSearchByLocationMap, variables, true);

    // Calculate total filter count
    const filterCount = totalFilterCount(defaultSelectedFilters, brandsClicked);
    setFiltersCount(filterCount);
    setFiltersPayload(defaultSelectedFilters);
    setFilterButtonClicked('');
  };

  const combinedBuckets = facets?.filter((filterObj: { type: { enumCode: string } }) =>
    LOCATIONS?.includes(filterObj?.type?.enumCode)
  );

  const regionFacets = facets?.filter(
    (filterObj: { type: { enumCode: string } }) => 'LEISURE_REGIONS' === filterObj?.type?.enumCode
  );

  const componentMap = {
    LEISURE_REGIONS: (filterData: any) => (
      <DropDownComponent
        regions={regionFacets}
        defaultValue={defaultSelectedFilters[defaultSelectedFilters?.length - 1]?.dimensions[0]}
        onClick={(region: { code: string }) => {
          handleFiltersSelectionChange(filterData?.type?.enumCode, region);
        }}
        clearButtonLabel={'Reset'}
        clearClickHandler={clearBtnHandler}
        viewPort={viewPort}
        textFieldLabel={'Region'}
      />
    ),
    BRANDS: (filterData: any) => (
      <BrandFilter
        title={viewPort !== 'viewPortL' && filterProps?.brands?.brandsLabel}
        isHotelBrandSelectedFlag={true}
        brandCategories={getCategorizedBrands(filterData?.buckets, filterProps?.brands?.brandCategories)}
        defaultSelected={showDefaultSelectedFilters(filterData)}
        onChange={(obj): void => handleFiltersSelectionChange(filterData?.type?.enumCode, obj)}
        clickTrackingLoc={'Filters'}
        className={filterProps?.brandTags?.length === 1 ? 'd-none' : ''}
        clickTrackingVal={`${filterData?.filterName} Section:`}
        // featureFlagLogo={true}
      />
    ),
    ACTIVITIES: (filterData: any) => (
      <BrandFilter
        title={viewPort !== 'viewPortL' && filterProps?.activities?.activitiesLabel}
        isHotelBrandSelectedFlag={true}
        brandCategories={getCategorizedBrands(
          filterData?.buckets,
          mapActivitiesToBrandsStructure(filterProps?.activities, filterProps?.activityTags || [])
        )}
        defaultSelected={showDefaultSelectedFilters(filterData)}
        onChange={(obj): void => handleFiltersSelectionChange(filterData?.type?.enumCode, obj)}
        clickTrackingLoc={'Filters'}
        clickTrackingVal={`${filterData?.filterName} Section:`}
        isActivities={true}
      />
    ),
    AMENITIES: (filterData: any) => (
      <CheckboxControlsList
        name={'amenities'}
        title={viewPort !== 'viewPortL' && filterProps?.amenities?.amenitiesLabel}
        controls={filterAvailableFilters(
          filterData?.buckets,
          filterProps?.amenityTags || [],
          filterProps?.amenities?.amenitiesList || []
        )}
        enableShowMoreToggle={true}
        onChange={(obj): void => handleFiltersSelectionChange(filterData?.type?.enumCode, obj)}
        showMoreCount={8}
        showSeparator={false}
        clickTrackingVal={`${filterData?.filterName} Section:`}
        defaultSelected={showDefaultSelectedFilters(filterData)}
        colLength={viewPort === 'viewPortL' ? MOBILE_COL_LENGTH : viewPort === 'viewPortM' ? COL_LENGTH : COL_LENGTH}
      />
    ),
    PROPERTY_TYPES: (filterData: any) => (
      <CheckboxControlsList
        name={'property-types'}
        title={viewPort !== 'viewPortL' && filterProps?.propertyTypeLabel}
        controls={filterAvailableFilters(filterData?.buckets, filterProps?.propertyTypeTags || [])}
        enableShowMoreToggle={true}
        onChange={(obj): void => handleFiltersSelectionChange(filterData?.type?.enumCode, obj)}
        showMoreCount={8}
        showSeparator={false}
        clickTrackingVal={`${filterData?.filterName} Section:`}
        defaultSelected={showDefaultSelectedFilters(filterData)}
        colLength={viewPort === 'viewPortL' ? MOBILE_COL_LENGTH : viewPort === 'viewPortM' ? COL_LENGTH : COL_LENGTH}
      />
    ),
    CITIES: () => (
      <DropDownComponent
        regions={combinedBuckets}
        defaultValue={
          defaultSelectedFilters[4]?.dimensions[0] ??
          defaultSelectedFilters[5]?.dimensions[0] ??
          defaultSelectedFilters[6]?.dimensions[0]
        }
        onClick={(region: { code: string }, type: { enumCode: string }) => {
          handleFiltersSelectionChange(type?.enumCode, region);
        }}
        viewPort={viewPort}
        textFieldLabel={'Location'}
      />
    ),
  };

  // show filters list on modal in specified order
  const showFiltersInSpecifiedOrder = (filtersOrderList: Array<{ filterName: string }>, filtersResponse: any) => {
    const newFiltersOrderList = filtersOrderList?.flatMap(filter => {
      return filter?.filterName === FILTERS_MAPPING?.Location ? LOCATION : filter;
    });

    if (filtersResponse?.length > 0) {
      // looping through the filterOrder props received from AEM or passed manually
      return newFiltersOrderList?.map((filter, index) => {
        // looping through the filter response and compare the filter code
        return filtersResponse?.map((_response: any) => {
          if (filter?.filterName === _response?.type?.enumCode) {
            const Component = componentMap[filter?.filterName as keyof typeof componentMap];
            if (Component) {
              return (
                <div key={index} className={filter?.filterName}>
                  {Component(_response)}
                </div>
              );
            }
            return <div></div>;
          } else {
            return <div></div>;
          }
        });
      });
    } else {
      return <div></div>;
    }
  };

  // reset the filter payload on modal
  const clearBtnHandler = () => {
    setBrandsClicked(false);
    // reset the filter payload
    const resetFilterPayload = updateFiltersPayload(filterProps);
    let payload = resetFilterPayload;

    if (filterButtonClicked !== 'Regions' && filterButtonClicked !== 'AllFilters') {
      payload = defaultSelectedFilters?.map((filter: { type: string }) => {
        if (filter.type === 'BRANDS' && filterButtonClicked === 'Brands') {
          return {
            type: 'BRANDS',
            dimensions: [...(filterProps?.brandTags || [])],
          };
        } else if (filter.type === 'AMENITIES' && filterButtonClicked === 'Amenities') {
          return {
            type: 'AMENITIES',
            dimensions: [...(filterProps?.amenityTags || [])],
          };
        } else if (filter.type === 'PROPERTY_TYPES' && filterButtonClicked === 'Property') {
          return {
            type: 'PROPERTY_TYPES',
            dimensions: [...(filterProps?.propertyTypeTags || [])],
          };
        } else if (filter.type === 'ACTIVITIES' && filterButtonClicked === 'Activities') {
          return {
            type: 'ACTIVITIES',
            dimensions: [...(filterProps?.activityTags || [])],
          };
        } else {
          return filter;
        }
      });
    }

    const variables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...payload],
        },
      },
      limit: 20,
      offset: 0,
      sort: null,
    };

    getData(phoenixLuxuryDatedSearchByLocationMapFilters, variables);

    setDefaultSelectedFilters(resetFilterPayload);

    // setFiltersPayload(resetFilterPayload);
  };

  // reset the filters payload to it's previous state on close of modal
  const resetFiltersPayloadOnClose = () => {
    const variables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...(prevfiltersRef?.current ?? [])],
        },
      },
      limit: 20,
      offset: 0,
      sort: null,
    };

    getData(phoenixLuxuryDatedSearchByLocationMapFilters, variables);
  };

  // reset the filters payload to it's previous state on click outside of modal
  const resetFiltersPayloadOnClickOutside = () => {
    const variables = {
      search: {
        options: { numberInParty: 1 },
        facets: {
          terms: [...prevfiltersRefVal],
        },
      },
      limit: 20,
      offset: 0,
      sort: null,
    };

    if (
      filterProps?.brandTags &&
      filterProps?.brandTags?.length > 1 &&
      prevfiltersRefVal[0]?.dimensions?.length !== filterProps?.brandTags?.length
    ) {
      setBrandsClicked(true);
    } else {
      setBrandsClicked(false);
    }
    getData(phoenixLuxuryDatedSearchByLocationMapFilters, variables);
  };

  const getModalData = () => {
    const filtersMap: any = FILTERS_MAPPING;
    const filterName = filtersMap?.[filterButtonClicked];

    if (filterButtonClicked === 'AllFilters') {
      const filtersOrderList =
        filterProps?.filtersOrderList?.length > 0
          ? getFilteredOrder(filterProps, filterProps?.filtersOrderList)
          : getFilteredOrder(filterProps, DEFAULT_FILTERS_ORDER);

      return showFiltersInSpecifiedOrder(filtersOrderList, facets);
    } else if (filterName) {
      const data = showFiltersInSpecifiedOrder(
        [
          {
            filterName,
          },
        ],
        facets
      );
      return data;
    } else {
      return <div></div>;
    }
  };

  useEffect(() => {
    const MAPPINGS: any = FILTERS_MAPPING;
    const pillElement = document?.getElementById(MAPPINGS[filterButtonClicked]);
    const pillRect = pillElement?.getBoundingClientRect();

    if (viewPort === 'viewPortL' && filterPanelRef.current && pillRect && filterButtonClicked) {
      filterPanelRef.current.style.position = 'absolute';

      filterPanelRef.current.style.top = `${
        pillRect.bottom +
        window.scrollY +
        (filterButtonClicked === 'Regions' || filterButtonClicked === 'Location' ? -16 : 0)
      }px`;
      filterPanelRef.current.style.left = `${pillRect.left + window.scrollX}px`;
      filterPanelRef.current.style.zIndex = 2;
      filterPanelRef.current.focus();
    }

    const handleScroll = () => {
      /**
       * condition to check the popup is opened or not in desktop view
       */
      if (popupOpenState === true && viewPort === 'viewPortL') {
        resetFiltersPayloadOnClickOutside();
        setPopupOpenState(false);
        setFilterButtonClicked('');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [filterButtonClicked, viewPort]);

  return (
    <StyledSearchResultsFilterPanel
      data-testid="luxury-SearchResultsFilterPanel"
      data-component-name="m-luxury-SearchResultsFilterPanel"
      ref={filterPanelRef}
      tabIndex={viewPort === 'viewPortL' ? -1 : 0}
      onBlur={e => {
        if (!dropdownRef.current?.contains(e?.relatedTarget) && viewPort === 'viewPortL') {
          resetFiltersPayloadOnClickOutside();
          setPopupOpenState(false);
          setFilterButtonClicked('');
        }
      }}
    >
      {viewPort !== 'viewPortL' ? (
        <Modal
          className={clsx(
            'custom-scrollbar',
            'modal-main-container',
            'popup-header-container',
            popupOpenState ? '' : 'close-popup-header'
          )}
          show={true}
          handleBlur={true}
          popupOpenState={popupOpenState}
          disableScrollOnBody={true}
          setPopupOpenState={setPopupOpenState}
          allFilterPopUp={true}
          role="dialog"
          aria-labelledby={'filtersHeaderLabel'}
          eventClass="radio-input checkbox-input region-el region-text m-icon-text-block"
          onClose={(): void => {
            resetFiltersPayloadOnClickOutside();
            setPopupOpenState(false);
            setFilterButtonClicked('');
          }}
          modalId={'filtersHeaderLabel'}
          aria-label="Suites Filter Options"
        >
          <Modal.Header
            className={clsx('modal-header px-3 py-3')}
            labelText={'All Filters'}
            popupHeaderOnCLoseFunc={(): void => {
              resetFiltersPayloadOnClose();
              setPopupOpenState(false);
              setFilterButtonClicked('');
            }}
          />

          <Modal.Body className={clsx('all-filters', 'custom-scrollbar', 'px-3 py-3', 'searchResultsFilter_panel')}>
            {getModalData()}
          </Modal.Body>

          <Modal.Footer
            clearClickHandler={clearBtnHandler}
            setPopUpOpenState={setPopupOpenState}
            applyBtnClickHandler={applyBtnHandler}
            clearButtonLabel={resetLabel}
            applyLabel={doneLabel}
            applyButtonDisabled={false}
            className={clsx('modal-footer px-3 py-3')}
            clickTrackingLoc={'Suites_Filter_Modal'}
            clearBtnClassname={'m-link modal-clear-btn'}
          />
        </Modal>
      ) : (
        <>
          {filterButtonClicked && (
            <div className="filter-dropdown color-scheme1">
              {filterButtonClicked === 'Regions' || filterButtonClicked === 'Location' ? (
                <div className="region-filter" ref={dropdownRef}>
                  {getModalData()}
                </div>
              ) : (
                <div ref={dropdownRef}>
                  <DropDownModal
                    dropDownChildrenClassName="col-12 px-1"
                    isAutoScrollEnabled={true}
                    dropDownSpecificFlag={false}
                    scrollDownModalCloseState={false}
                    labelledBy="luxury-dropdown"
                    show={true}
                    className={`dropdown-container custom-map-dropdown`}
                    childrenClassName={'luxury-dropdown-main'}
                    dropdownModalOpenState={popupOpenState}
                    setDropdownModalOpenState={setPopupOpenState}
                    applyBtnClickHandler={applyBtnHandler}
                    applyLabel={doneLabel}
                    clearButtonLabel={resetLabel}
                    desktopDropdown={true}
                    mobileFooterEnabled={true}
                    clearClickHandler={clearBtnHandler}
                  >
                    <div className="filter-popup mt-3">{getModalData()}</div>
                  </DropDownModal>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </StyledSearchResultsFilterPanel>
  );
};
